// apps/web/src/hooks/usePushNotifications.ts

import { useState, useEffect, useCallback, useRef } from 'react';
import { pushNotificationService } from '@/services/pushNotifications';
import { trpc } from '@/trpc/client';
import { useAuthStore } from '@/store/authStore';
import { toast } from 'sonner';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';

export const usePushNotifications = () => {
  const { user } = useAuthStore();
  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const hasInitialized = useRef(false);

  // Mutation to save subscription to backend
  const savePushSubscription = trpc.notification.savePushSubscription?.useMutation();
  const removePushSubscription = trpc.notification.removePushSubscription?.useMutation();

  const isNative = Capacitor.isNativePlatform();

  // Initialize
  useEffect(() => {
    const init = async () => {
      if (!user || hasInitialized.current) {
        setIsLoading(false);
        return;
      }

      if (isNative) {
        // Native Initialization
        setIsSupported(true);

        // Check initial permission
        const permStatus = await PushNotifications.checkPermissions();
        setPermission(permStatus.receive as NotificationPermission); // 'granted' | 'denied' | 'prompt'

        if (permStatus.receive === 'granted') {
          setIsSubscribed(true); // Assumption: If granted, we are likely registered
        }

        // Register Native Listeners
        PushNotifications.addListener('registration', (token) => {
          console.log('📲 Native Push Token:', token.value);
          // TODO: Send this simple token to backend if utilizing FCM directly
          // For now, we assume the backend handles WebPush structure, 
          // but we might need a separate endpoint for raw FCM tokens in the future.
          // For this hybrid approach, we'll mark as subscribed.
          setIsSubscribed(true);
        });

        PushNotifications.addListener('registrationError', (error) => {
          console.error('❌ Native Registration Error:', error);
          setIsSubscribed(false);
        });

        PushNotifications.addListener('pushNotificationReceived', (notification) => {
          console.log('🔔 Native Notification Received:', notification);
          toast.info(notification.title || 'New Notification');
        });

        PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
          console.log('👆 Native Notification Clicked:', notification);
          // Navigate if needed
        });

      } else {
        // Web Initialization
        const supported = pushNotificationService.isSupported();
        setIsSupported(supported);
        if (supported) {
          setPermission(pushNotificationService.getPermissionStatus());
          // Check web subscription
          const sub = await pushNotificationService.getSubscription();
          setIsSubscribed(!!sub);
        }
      }

      hasInitialized.current = true;
      setIsLoading(false);
    };

    init();

    return () => {
      if (isNative) {
        PushNotifications.removeAllListeners();
      }
    };
  }, [user, isNative]);


  // Enable Logic (Hybrid)
  const enable = useCallback(async () => {
    setIsLoading(true);
    try {
      if (isNative) {
        console.log('📱 Requesting Native Permissions...');
        const result = await PushNotifications.requestPermissions();

        if (result.receive === 'granted') {
          setPermission('granted');
          await PushNotifications.register();
          setIsSubscribed(true);
          toast.success('Push notifications enabled!');
          return true;
        } else {
          setPermission('denied');
          toast.error('Permission denied');
          return false;
        }

      } else {
        // Web Logic
        if (!isSupported) return false;

        const newPermission = await pushNotificationService.requestPermission();
        setPermission(newPermission);

        if (newPermission === 'granted') {
          const sub = await pushNotificationService.subscribe();
          if (sub) {
            await savePushSubscription.mutateAsync({ subscription: JSON.parse(JSON.stringify(sub)) });
            setIsSubscribed(true);
            toast.success('Push notifications enabled!');
            return true;
          }
        }
        return false;
      }
    } catch (e) {
      console.error('Failed to enable notifications:', e);
      toast.error('Failed to enable notifications');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [isNative, isSupported, savePushSubscription]);


  // Disable Logic (Hybrid)
  const disable = useCallback(async () => {
    setIsLoading(true);
    try {
      if (isNative) {
        // Native doesn't really "unregister" easily, but we can simulate state
        // Commonly we just stop sending to backend, but for native we just clear state here
        setIsSubscribed(false);
        toast.info('Notifications disabled (Native)');
        // In a real app, you might call a backend endpoint to remove the FCM token
        return true;
      } else {
        const success = await pushNotificationService.unsubscribe();
        if (success) {
          await removePushSubscription.mutateAsync();
          setIsSubscribed(false);
          return true;
        }
        return false;
      }
    } catch (e) {
      console.error(e);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [isNative, removePushSubscription]);

  return {
    isSupported,
    permission,
    isSubscribed,
    isLoading,
    enable,
    disable,
    showTestNotification: async () => { }, // Not relevant for hook consumer usually
    checkSubscription: async () => isSubscribed
  };
};